import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  Calculator,
  CalendarDays,
  Camera,
  CheckCircle2,
  Settings,
  Shield,
  Timer,
  Upload,
} from "lucide-react";
import React, { useState } from "react";
import RoutesInCycleTable from "./tabComponents/RoutesInCycleTable";
import CycleReadingsTable from "./tabComponents/CycleReadingsTable";
import { UploadMissingReadingsDialog } from "../dialogs/UploadMissingReadingsDialog";
import { EstimateMissingReadingsDialog } from "../dialogs/EstimateMissingReadingsDialog";
import { RunValidationsDialog } from "../dialogs/RunValidationsDialog";
import { MarkAsCompleteDialog } from "../dialogs/MarkAsCompleteDialog";
import ManageAssignmentDialog from "@features/mx/validations/components/ValidationDashboard/ManageAssignmentDialog";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useNavigate, useParams } from "react-router-dom";
import {
  useMeterRunDetail,
  usePhotoMeterGetRules,
} from "@features/mx/meterReads/hooks";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { KeyStatsCard } from "./keyStats";
import { logEvent } from "@shared/analytics/analytics";
import {
  useAuditUsers,
  usePostMeterCycleAuditUserMapping,
  usePutMeterCycleAuditUserMapping,
} from "@features/mx/validations/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { PageHeader } from "@shared/components/PageHeader";

function PhotoReadDetail() {
  const { id } = useParams<{ id: string }>();
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  const { data: meterRunData, refetch: refetchDetails } = useMeterRunDetail({
    meter_run_id: id,
    remote_utility_id: remoteUtilityId,
    meter_type: "PHOTO_METER",
  });

  const runDetail = meterRunData?.result;
  const metricsValues = runDetail?.metricsPercentages;
  const metricsChanges = runDetail?.metricsComparison;

  const validators = runDetail?.userData?.validatorInfo?.map(
    (validator) => validator.validatorName
  );
  const supervisors = runDetail?.userData?.supervisorInfo?.map(
    (supervisor) => supervisor.supervisorName
  );
  const meterReaders = runDetail?.userData?.meterReaderInfo?.map(
    (meterReader) => meterReader.meterReaderName
  );

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [validationsOpen, setValidationsOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState(0);
  const [exemptedAfterValidation, setExemptedAfterValidation] = useState<
    number | null
  >(null);
  const [completeOpen, setCompleteOpen] = useState(false);

  // Add: estimation modal state
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [selectedEstimationRule, setSelectedEstimationRule] = useState<
    string | null
  >(null);

  // Add: assignment dialog state
  const [auditUserSearch, setAuditUserSearch] = useState("");

  const { data: estimationRulesData } = usePhotoMeterGetRules(
    1,
    estimateOpen,
    runDetail?.meterSchedule?.meterCycle?.id
  );
  const [openDialog, setOpenDialog] = useState<
    null | "validator" | "supervisor"
  >(null);

  const userRoleType =
    openDialog === "validator" ? 0 : openDialog === "supervisor" ? 2 : 0; // 0 for validator | 2 for supervisor

  const cycleId = runDetail?.meterSchedule?.meterCycle?.id;

  const { data: validatorsList } = useAuditUsers(
    remoteUtilityId,
    userRoleType,
    openDialog === "validator",
    cycleId,
    auditUserSearch
  );
  const { data: supervisorList } = useAuditUsers(
    remoteUtilityId,
    userRoleType,
    openDialog === "supervisor",
    cycleId,
    auditUserSearch
  );
  const postMeterCycleAuditUserMappingMutation =
    usePostMeterCycleAuditUserMapping(userRoleType, cycleId);
  const putMeterCycleAuditUserMappingMutation =
    usePutMeterCycleAuditUserMapping(userRoleType, cycleId);

  const handleAssign = (row: any) => {
    const payload = {
      meterCycle: cycleId,
      audit_user: row?.id,
      remote_utility_id: remoteUtilityId,
    };
    postMeterCycleAuditUserMappingMutation.mutate(payload, {
      onSuccess: () => {
        logEvent("MX Validator Added", payload);
        refetchDetails();
      },
    });
  };

  const handleUnassign = (row: any) => {
    const payload = {
      meter_cycle: cycleId,
      audit_user: row?.id,
      remote_utility_id: remoteUtilityId,
    };
    putMeterCycleAuditUserMappingMutation.mutate(payload, {
      onSuccess: () => {
        logEvent("MX Validator Removed", payload);
        refetchDetails();
      },
    });
  };

  const tabComponents = {
    routes: {
      label: "Routes",
      component: <RoutesInCycleTable />,
    },
    reading: {
      label: "Readings",
      component: <CycleReadingsTable />,
    },
  };

  const SectionBlock = ({ title, children }) => (
    <div className="space-y-2 p-2 border rounded-md">
      <h3 className="text-sm font-semibold tracking-wide">{title}</h3>
      {children}
    </div>
  );

  const GridBlock = ({ items }) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {items.map((item) => (
        <ValueLabel
          key={item.label}
          label={item.label}
          value={item.value}
          tooltip={item.tip}
          description={item.description}
          layout="vertical"
          className=""
        />
      ))}
    </div>
  );

  const ActionValue = ({ count, onClick }) => (
    <span className="inline-flex items-center">
      {`${count} member(s)`}
      <Button
        variant="ghost"
        onClick={onClick}
        className="inline-flex items-center text-muted-foreground hover:text-foreground p-2"
      >
        <Settings className="h-4 w-4" />
      </Button>
    </span>
  );
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        {/* Back Arrow */}
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700"
          onClick={() => navigate("/mx/meterread/photo-meter")}
        />

        {/* Page Header */}
        <div className="flex flex-col">
          <PageHeader
            title="View Photo Meter Read"
            subtitle="Track performance and progress of your Photo Meter Read"
          />
        </div>
      </div>
      <div className="flex flex-col lg:flex-row">
        {/* Left Column */}
        <div className="mr-4 w-full lg:w-[70%] space-y-6">
          {/* Cycle Details Card */}
          <Card className="overflow-hidden">
            {/* ================= HEADER ================= */}
            <div className="border-b pr-4">
              <CardHeader className="flex flex-row items-center justify-between py-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full grid place-items-center text-blue-500">
                    <CalendarDays className="h-6 w-6 text-blue-500" />
                  </div>

                  <div>
                    <CardTitle className="text-2xl tracking-tight">
                      {runDetail?.meterSchedule?.meterCycle?.meterCycleName}
                    </CardTitle>

                    <p className="text-xs text-muted-foreground">
                      Reading Period: {runDetail?.startDate} -{" "}
                      {runDetail?.endDate}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Badge>{runDetail?.isActive ? "Active" : "Inactive"}</Badge>
                  <CustomTooltip message="Overview of the selected meter reading cycle. Review core information and status." />
                </div>
              </CardHeader>
            </div>

            {/* ================= BODY CONTENT ================= */}
            <CardContent className="pt-4 space-y-2">
              {/* ===== READ CONFIGURATION ===== */}
              <SectionBlock title="READ CONFIGURATION">
                <GridBlock
                  items={[
                    {
                      label: "Read Type",
                      value: runDetail?.meterSchedule?.meterCycle?.meterType,
                      tip: "Method used for collecting meter readings.",
                    },
                    {
                      label: "Utility Service",
                      value:
                        runDetail?.meterSchedule?.meterCycle?.productCode?.join(
                          ", "
                        ),
                      tip: "Type of utility service measured.",
                    },
                    {
                      label: "Month/Period",
                      value: runDetail?.month,
                      tip: "Billing period this cycle belongs to.",
                    },
                    {
                      label: "Cycle Duration",
                      value: `${runDetail?.cycleDuration} days`,
                      tip: "Total duration for this cycle.",
                    },
                    {
                      label: "Reading Period",
                      value: `${runDetail?.startDate} - ${runDetail?.endDate}`,
                      tip: "Active period for this reading cycle.",
                    },
                  ]}
                />
              </SectionBlock>

              {/* ===== COVERAGE & SCALE ===== */}
              <SectionBlock title="COVERAGE & SCALE">
                <GridBlock
                  items={[
                    {
                      label: "Number of Routes",
                      value: runDetail?.routeCount,
                      tip: "Total assigned routes.",
                    },
                    {
                      label: "Number of Meters",
                      value: runDetail?.meterCount,
                      tip: "Total meters in this cycle.",
                    },
                    {
                      label: "Number of Consumers",
                      value: runDetail?.consumerCount,
                      tip: "Total unique consumers.",
                    },
                  ]}
                />
              </SectionBlock>

              {/* ===== ADMINISTRATION & TEAM ASSIGNMENT ===== */}
              <SectionBlock title="ADMINISTRATION & TEAM ASSIGNMENT">
                <GridBlock
                  items={[
                    {
                      label: "Meter Readers",
                      value: `${meterReaders?.length} member(s)`,
                      tip: `Readers: ${meterReaders?.join(", ")}`,
                    },
                    {
                      label: "Validators",
                      value: (
                        <ActionValue
                          count={validators?.length}
                          onClick={() => setOpenDialog("validator")}
                        />
                      ),
                      tip: `Validators: ${validators?.join(", ")}`,
                    },
                    {
                      label: "Supervisors",
                      value: (
                        <ActionValue
                          count={supervisors?.length}
                          onClick={() => setOpenDialog("supervisor")}
                        />
                      ),
                      tip: `Supervisors: ${supervisors?.join(", ")}`,
                    },
                    {
                      label: "Created By",
                      value: runDetail?.meterSchedule?.createdUserRemoteName,
                      tip: "User who initiated this cycle.",
                    },
                    {
                      label: "Created On",
                      value: runDetail?.createdDate,
                      tip: "Date/time cycle was created.",
                    },
                  ]}
                />
              </SectionBlock>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="lg:w-[30%] space-y-4">
          {/* Key Stats Card - UPDATED: show meters, readings, missing, dispatched/total routes */}
          <KeyStatsCard
            data={{
              totalMeters: runDetail?.meterCount,
              collectedReadings: runDetail?.readingStats?.totalReadingCount,
              missingReadings: runDetail?.missingReadingCount,
              dispatchedRoutes: runDetail?.routeDispatchedCount,
              totalRoutes: runDetail?.routeCount,
            }}
          />
          {/* Quick Actions */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-start gap-4">
              <CardTitle className="text-base">Quick Actions</CardTitle>
              <div className="pt-1">
                <CustomTooltip
                  message="Common operations for managing this reading cycle. Actions
                    are role-based - you'll only see options you have permission
                    to perform."
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {[
                {
                  label: "Upload Missing Readings",
                  icon: Upload,
                  tip: "Bulk import readings from CSV/Excel file. Useful for offline readings or data migration. File must match template format: Meter ID, Reading Value, Reading Date, Photo Reference.",
                },
                {
                  label: "Estimate Missing Readings",
                  icon: Calculator,
                  tip: "Generate estimated readings for meters that couldn't be accessed (RCNT status). Uses AI model based on historical consumption patterns and seasonal factors. Estimates are flagged for future verification.",
                },
                // {
                //   label: "Run Validations",
                //   icon: Shield,
                //   tip: "Manually trigger validation checks on all submitted readings. Identifies anomalies like: negative consumption, readings lower than previous, extreme spikes, missing photos.",
                // },
                {
                  label: "Mark as Complete",
                  icon: CheckCircle2,
                  tip: "Finalize this cycle when all necessary readings and validations are done. This moves the cycle to History and locks further modifications.",
                },
              ].map((a) => (
                <div
                  key={a.label}
                  className="flex items-center justify-between w-full"
                >
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    tooltip={a.tip}
                    tooltipPosition="top"
                    onClick={() => {
                      if (a.label === "Upload Missing Readings") {
                        setUploadOpen(true);
                        return;
                      }
                      if (a.label === "Estimate Missing Readings") {
                        setSelectedEstimationRule(null);
                        setEstimateOpen(true);
                        return;
                      }
                      if (a.label === "Run Validations") {
                        setExemptedAfterValidation(null);
                        setValidationProgress(0);
                        setIsValidating(false);
                        setValidationsOpen(true);
                        return;
                      }
                      if (a.label === "Mark as Complete") {
                        setCompleteOpen(true);
                        return;
                      }
                      // toast.info(`${a.label} clicked`);
                    }}
                  >
                    <a.icon className="h-4 w-4 mr-2" />
                    {a.label}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
      <Card className="border-none p-0">
        <CardContent className="p-0">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <QuickStatsCard
              title="Completion Rate"
              count={metricsValues?.completionRate}
              tooltip={
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Definition:</strong> Percentage of meters
                    successfully read vs total meters in cycle.
                  </p>
                  <p>
                    <strong>How to use:</strong> Track daily progress. If rate
                    is below target, consider reassigning routes or adding field
                    staff.
                  </p>
                  <p>
                    <strong>Calculation:</strong> (Number of meters read / Total
                    meters) × 100
                  </p>
                  <p>
                    <strong>Example:</strong> (1 / 1,303) × 100 = 0.08%
                  </p>
                  <p>
                    <strong>Target:</strong> Should reach 95%+ by cycle end
                    date.
                  </p>
                </div>
              }
              trendSummary={`${metricsChanges?.completionRateChange} from last cycle`}
              icon={<BarChart3 className="h-6 w-6 text-slate-600" />}
            />

            <QuickStatsCard
              title="Reading Accuracy"
              count={metricsValues?.readingAccuracy}
              tooltip={
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Definition:</strong> Percentage of readings that
                    passed validation rules without errors.
                  </p>
                  <p>
                    <strong>How to use:</strong> Monitor data quality. Low
                    accuracy indicates training needs or system issues.
                  </p>
                  <p>
                    <strong>Calculation:</strong> (Valid readings / Total
                    readings submitted) × 100
                  </p>
                  <p>
                    <strong>Example:</strong> (1 / 1) × 100 = 100%
                  </p>
                  <p>
                    <strong>Validation checks:</strong> Photo clarity, reading
                    value within expected range, timestamp validity, GPS
                    coordinates present.
                  </p>
                  <p>
                    <strong>Target:</strong> Maintain 95%+ accuracy.
                  </p>
                </div>
              }
              trendSummary={`${metricsChanges?.readingAccuracyChange} from last cycle`}
              icon={<CheckCircle2 className="h-6 w-6 text-blue-600" />}
            />

            <QuickStatsCard
              title="Days Remaining"
              count={metricsValues?.avgTurnaround}
              tooltip={
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Definition:</strong> Number of days left until the
                    cycle due date.
                  </p>
                  <p>
                    <strong>How to use:</strong> Track urgency. If 0, the cycle
                    is due or overdue — consider prioritizing remaining work.
                  </p>
                  <p>
                    <strong>Calculation:</strong> max(0, ceil((End Date - Now) /
                    1 day))
                  </p>
                  <p>
                    <strong>Note:</strong> Shows 0 when due date has passed.
                  </p>
                </div>
              }
              trendSummary={`${metricsChanges?.avgTurnaroundChange} from last cycle`}
              icon={<Timer className="h-6 w-6 text-amber-600" />}
            />

            <QuickStatsCard
              title="Photo Submission"
              count={metricsValues?.photoSubmission}
              tooltip={
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Definition:</strong> Percentage of readings with
                    valid photo documentation.
                  </p>
                  <p>
                    <strong>How to use:</strong> Ensures audit compliance and
                    dispute resolution capability. Required for billing
                    verification.
                  </p>
                  <p>
                    <strong>Calculation:</strong> (Readings with photos / Total
                    readings) × 100
                  </p>
                  <p>
                    <strong>Example:</strong> (1 / 1) × 100 = 100%
                  </p>
                  <p>
                    <strong>Requirements:</strong> Photo must show meter display
                    clearly, include full meter serial number, and be taken
                    within 24 hours of reading submission.
                  </p>
                  <p>
                    <strong>Target:</strong> Must maintain 100% for regulatory
                    compliance.
                  </p>
                </div>
              }
              trendSummary={`${metricsChanges?.photoSubmissionChange} from last cycle`}
              icon={<Camera className="h-6 w-6 text-purple-600" />}
            />
          </div>
        </CardContent>
      </Card>
      <Tabs
        defaultValue="routes"
        tabComponents={tabComponents}
        tabsListClassName="grid grid-cols-2 gap-2"
        className="space-y-4"
      />
      <UploadMissingReadingsDialog open={uploadOpen} setOpen={setUploadOpen} />

      <EstimateMissingReadingsDialog
        open={estimateOpen}
        setOpen={setEstimateOpen}
        missingReadingsCount={runDetail?.missingReadingCount || 0}
        estimationRulesData={estimationRulesData}
      />

      <RunValidationsDialog
        open={validationsOpen}
        setOpen={setValidationsOpen}
        scheduleId={runDetail?.meterSchedule?.id}
        newReadingsPending={runDetail?.pendingCount}
      />

      <MarkAsCompleteDialog open={completeOpen} setOpen={setCompleteOpen} />

      {openDialog && (
        <ManageAssignmentDialog
          open={!!openDialog}
          onOpenChange={(open) => !open && setOpenDialog(null)}
          type={openDialog}
          data={openDialog === "validator" ? validatorsList : supervisorList}
          onAssign={handleAssign}
          onUnassign={handleUnassign}
          onSearch={(search) => {
            setAuditUserSearch(search);
          }}
        />
      )}
    </div>
  );
}

export default PhotoReadDetail;
